import translate from './applyTranslation';
const server = true ? window.location.origin : 'https://staging.cheap.cust.commerz.se';
const parseResponseBody = async (response) => {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return {
      message: text,
    };
  }
};

const getErrorMessage = (responseBody, fallback) => {
  const nestedError = responseBody?.error;

  if (typeof nestedError === 'object' && nestedError !== null) {
    return (
      nestedError.message ||
      responseBody?.message ||
      fallback
    );
  }

  return (
    responseBody?.message ||
    nestedError ||
    responseBody?.statusMessage ||
    fallback
  );
};

const serializeError = (error) => ({
  name: error?.name,
  message: error?.message || error?.error,
  status: error?.status,
  code: error?.code,
  stack: error?.stack,
  details: error?.details,
});

const APICall = async (params) => {
  const { verb, url, body, access_token } = params;
  const myHeaders = new Headers();

  if (access_token) {
    myHeaders.append('Authorization', `Bearer ${access_token}`);
  }

  const requestOptions = {
    method: verb || 'get',
    headers: myHeaders,
    body,
  };

  const requestURL = `${server}/api/${url}`;

  try {
    const response = await fetch(requestURL, requestOptions);
    const handledResponse = await parseResponseBody(response);

    if (!response.ok) {
      const error = new Error(
        getErrorMessage(
          handledResponse,
          response.statusText || `HTTP ${response.status}`
        )
      );

      error.status = response.status;
      error.code =
        handledResponse?.code ||
        handledResponse?.error?.code ||
        `HTTP_${response.status}`;
      error.details = handledResponse;

      throw error;
    }

    if (handledResponse?.error) {
      const error = new Error(
        getErrorMessage(handledResponse, 'The API returned an error')
      );

      error.status = response.status;
      error.code =
        handledResponse?.code ||
        handledResponse?.error?.code ||
        'API_ERROR';
      error.details = handledResponse;

      throw error;
    }

    return handledResponse;
  } catch (error) {
    if (error.status || error.code) {
      throw {
        error: error.message,
        status: error.status,
        code: error.code,
        details: error.details,
        err: JSON.stringify(error.details || error),
      };
    }

    if (error.name === 'AbortError') {
      throw {
        error: 'The request timed out.',
        code: 'REQUEST_TIMEOUT',
        err: error.message,
      };
    }

    if (error instanceof TypeError) {
      throw {
        error: 'Unable to connect to the server.',
        code: 'NETWORK_ERROR',
        err: error.message,
      };
    }

    throw {
      error: error.message || error.error || translate('', 'generic_api_call_error'),
      status: error.status,
      code: error.code || 'CLIENT_ERROR',
      details: error.details,
      err: JSON.stringify(serializeError(error)),
    };
  }
};

export const APIDownload = async (params) => {
  let { verb, url, body, access_token } = params;
  const myHeaders = new Headers();
  if (access_token) {
    myHeaders.append("Authorization", `Bearer ${access_token}`);
  }

  const requestOptions = {
    method: verb || 'get',
    headers: myHeaders,
    body: body,
    redirect: 'follow'
  };

  const requestURL = `${server}/api/${url}`;
  const response = await fetch(requestURL, requestOptions);
  const responseBlob = await response.blob();
  if (response.status >= 400) {
    // eslint-disable-next-line no-throw-literal
    throw {
      error: response.statusText,
      err: await responseBlob.text()
    };
  }

  // Try to extract filename from Content-Disposition
  const contentDisposition = response.headers.get("Content-Disposition");
  const contentType = response.headers.get("Content-Type") || responseBlob.type || "";
  let filename = "download";
  if (contentDisposition && contentDisposition.includes("filename=")) {
    filename = contentDisposition
      .split("filename=")[1]
      .split(";")[0]
      .replace(/['"]/g, ""); // remove quotes if present
  }

  const isPdfResponse = contentType.includes("application/pdf") || /pdf/i.test(filename) || /\/pdf(\?|$)/i.test(url);
  const blob = isPdfResponse && responseBlob.type !== "application/pdf"
    ? new Blob([responseBlob], { type: "application/pdf" })
    : responseBlob;

  return { blob, filename };
};

export default APICall;
